import { authAudienceConfigured, decodeJwtPayload, jwtIsFresh, portalApiTokenOptions } from './authSession';

/** Attach Auth0 bearer token for verified billing API calls. */
export async function billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims } = {}) {
    const headers = {};

    const acceptBearer = (token) => {
        if (typeof token !== 'string' || !token || !jwtIsFresh(token)) {
            return false;
        }
        headers.Authorization = `Bearer ${token}`;
        return true;
    };

    const tokenFromSilent = (result) => {
        if (result && typeof result === 'object') {
            return result.id_token || result.access_token || '';
        }
        return result;
    };

    const attachIdToken = async () => {
        if (getAccessTokenSilently) {
            for (const cacheMode of [undefined, 'off']) {
                try {
                    const detailed = await getAccessTokenSilently({
                        ...portalApiTokenOptions({ cacheMode }),
                        detailedResponse: true,
                    });
                    if (acceptBearer(tokenFromSilent(detailed))) {
                        return true;
                    }
                } catch (err) {
                    console.warn('Billing API: could not obtain ID token via detailedResponse', err);
                }
            }
        }
        if (!getIdTokenClaims) {
            return false;
        }
        try {
            const claims = await getIdTokenClaims();
            if (acceptBearer(claims?.__raw)) {
                return true;
            }
        } catch (err) {
            console.warn('Billing API: could not obtain ID token', err);
        }
        return false;
    };

    const attachAccessToken = async () => {
        if (!getAccessTokenSilently) {
            return false;
        }
        for (const cacheMode of [undefined, 'off']) {
            try {
                const token = await getAccessTokenSilently(portalApiTokenOptions({ cacheMode }));
                if (typeof token !== 'string' || !token) {
                    continue;
                }
                if (decodeJwtPayload(token) && !jwtIsFresh(token)) {
                    continue;
                }
                headers.Authorization = `Bearer ${token}`;
                return true;
            } catch (err) {
                console.warn('Billing API: could not obtain access token', err);
            }
        }
        return false;
    };

    // Without an API audience (staging/prod default), SPA access tokens are opaque or
    // lack an email claim — backend must call Auth0 /userinfo, which is flaky under
    // concurrent load. ID tokens carry a verified email and are reliable here.
    if (!authAudienceConfigured()) {
        if (await attachIdToken()) {
            return headers;
        }
    } else {
        // With an API audience, only API access tokens carry read:contract-state.
        if (await attachAccessToken()) {
            return headers;
        }
    }

    throw new Error('Sign in required to access billing. Try logging out and back in.');
}

/** Best-effort Auth0 bearer for verified backend routes; returns {} when unavailable. */
export async function optionalAuthHeaders({ getAccessTokenSilently, getIdTokenClaims } = {}) {
    try {
        return await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
    } catch (err) {
        console.warn('API: could not obtain auth token', err);
        return {};
    }
}
